import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  FileText,
  Search,
  Filter,
  Eye,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  Euro,
  MessageSquare,
  CreditCard,
  AlertTriangle,
  Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

// Interfaces
interface QuoteItem { description: string; quantity: number; unitPrice: number; itemType: string; }
interface Quote {
  _id: string; reference: string; title: string; description: string; status: any;
  items: QuoteItem[]; subtotal: number; taxRate: number; taxAmount: number; total: number;
  validUntil: string; createdAt: string; updatedAt: string;
  clientId: { _id: string; firstName: string; lastName: string; email: string; };
  technicianId?: { _id: string; firstName: string; lastName: string; };
}

const ClientQuotes: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchQuotes = async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const response = await apiService.quotes.getMy();
      setQuotes(response.data?.data?.quotes || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthLoading) {
      fetchQuotes();
    }
  }, [isAuthenticated, isAuthLoading]);

  const handleRespond = async (quoteId: string, accepted: boolean) => {
    const originalQuotes = [...quotes];
    const optimisticUpdate = quotes.map(q => 
      q._id === quoteId ? { ...q, status: accepted ? 'accepted' : 'rejected' } : q
    );
    setQuotes(optimisticUpdate);

    try {
      await apiService.quotes.respond(quoteId, accepted);
      toast.success(`Devis ${accepted ? 'accepté' : 'refusé'} avec succès !`);
      await fetchQuotes(); // Re-fetch to get the definitive state
    } catch (error) {
      setQuotes(originalQuotes); // Revert on error
      toast.error('Erreur lors de la mise à jour du devis.');
    }
  };

  // --- UI & Helper functions ---

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Brouillon', icon: FileText },
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Envoyé', icon: Send },
      accepted: { color: 'bg-green-100 text-green-800', label: 'Accepté', icon: CheckCircle2 },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Refusé', icon: XCircle },
      expired: { color: 'bg-yellow-100 text-yellow-800', label: 'Expiré', icon: AlertTriangle },
      mission_assigned: { color: 'bg-purple-100 text-purple-800', label: 'Mission Assignée', icon: Clock },
    };
    const config = statusConfig[status] || { color: 'bg-gray-200', label: status, icon: AlertTriangle };
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const renderStats = () => {
    const total = quotes.length;
    const sent = quotes.filter(q => q.status === 'sent').length;
    const accepted = quotes.filter(q => q.status === 'accepted').length;
    const totalAmount = quotes.reduce((sum, q) => sum + q.total, 0);

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6"><p className="text-sm font-medium text-gray-600">Total Devis</p><p className="text-2xl font-bold">{total}</p></Card>
        <Card className="p-6"><p className="text-sm font-medium text-gray-600">En Attente</p><p className="text-2xl font-bold text-yellow-600">{sent}</p></Card>
        <Card className="p-6"><p className="text-sm font-medium text-gray-600">Acceptés</p><p className="text-2xl font-bold text-green-600">{accepted}</p></Card>
        <Card className="p-6"><p className="text-sm font-medium text-gray-600">Montant Total</p><p className="text-2xl font-bold text-blue-600">{totalAmount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p></Card>
      </div>
    );
  };

  if (isAuthLoading || isLoading) {
    return <Layout><div>Chargement...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Mes Devis</h1>
          <p className="text-gray-600">Consultez et gérez vos devis reçus</p>
        </div>
        
        {renderStats()}

        <div className="space-y-6">
          {quotes.length > 0 ? (
            quotes.map((quote) => (
              <Card key={quote._id} className="p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">{quote.title}</h3>
                    <p className="text-sm text-gray-500">{quote.reference}</p>
                    <p className="text-sm text-gray-500">Créé le: {new Date(quote.createdAt).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getStatusBadge(quote.status)}
                    <p className="text-2xl font-bold text-gray-900">{quote.total.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/client/quotes/${quote._id}`)}>
                    <Eye className="w-4 h-4 mr-2" />
                    Voir Détails
                  </Button>
                  {quote.status === 'sent' && (
                    <>
                      <Button variant="danger_outline" size="sm" onClick={() => handleRespond(quote._id, false)}>
                        <XCircle className="w-4 h-4 mr-2" />
                        Refuser
                      </Button>
                      <Button variant="success" size="sm" onClick={() => handleRespond(quote._id, true)}>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Accepter
                      </Button>
                    </>
                  )}
                  {quote.status === 'accepted' && (
                     <Button variant="primary" size="sm" onClick={() => navigate(`/client/payment/${quote._id}`)}>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Payer la facture
                      </Button>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-10 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto text-gray-400" />
              <h3 className="mt-4 text-lg font-medium">Aucun devis trouvé</h3>
              <p className="mt-1 text-sm">Vous n'avez aucun devis pour le moment. Lorsque des devis seront créés pour vous, ils apparaîtront ici.</p>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClientQuotes;
